/** A customers API failure, with the server's code kept (the prose alone loses it). */
export class CustomersApiError extends Error {
    constructor(message, opts = {}) {
        super(message);
        this.name = 'CustomersApiError';
        this.code = opts.code;
        this.status = opts.status;
        this.body = opts.body;
        Object.setPrototypeOf(this, CustomersApiError.prototype);
    }
    /** The field a VALIDATION_ERROR or CUSTOMER_DUPLICATE is about (e.g. phone, email). */
    get field() {
        const f = this.body?.field;
        return typeof f === 'string' ? f : undefined;
    }
    /** The existing customer a 409 CUSTOMER_DUPLICATE names. */
    get existingId() {
        const id = this.body?.existing_id;
        return typeof id === 'string' ? id : undefined;
    }
}
function customersError(res, fallback) {
    const body = res.errorBody && typeof res.errorBody === 'object' ? res.errorBody : undefined;
    const code = typeof body?.code === 'string' ? body.code : res.status ? undefined : 'NETWORK';
    return new CustomersApiError(res.error || fallback, { code, status: res.status, body });
}
/** Unwraps a customers response or throws a CustomersApiError (shared by the module's API files). */
export async function unwrapCustomers(pending, fallback) {
    const res = await pending;
    if (!res.success)
        throw customersError(res, fallback);
    return res.data;
}
/** True for the "module is off" answer (404 CUSTOMERS_OFF). */
export function isCustomersOff(err) {
    return err instanceof CustomersApiError && err.code === 'CUSTOMERS_OFF';
}
/** Words for a failure, for the shop. */
export function describeCustomersError(err) {
    if (err instanceof CustomersApiError) {
        switch (err.code) {
            case 'CUSTOMERS_OFF':
                return 'Customers is switched off for this account.';
            case 'CUSTOMER_NOT_FOUND':
                return 'That customer isn’t available at this store.';
            case 'CUSTOMER_DUPLICATE':
                return 'A customer with this phone number or email is already on the list.';
            case 'CUSTOMER_SHARED_ACCOUNT_WIDE':
                return 'Only someone who manages customers for every store can take a customer off All stores.';
            case 'WORK_ORDER_CLOSED':
                return 'This work order is closed, so its customer can’t change.';
            case 'NETWORK':
                return 'Couldn’t reach the server. Check your connection and try again.';
            default:
                return err.message;
        }
    }
    return err instanceof Error ? err.message : 'Something went wrong.';
}
const base = (locationId) => `/locations/${encodeURIComponent(locationId)}/customers`;
/** One customer's path at a store (shared by the module's API files). */
export const customerPath = (locationId, customerId) => `${base(locationId)}/${encodeURIComponent(customerId)}`;
/** A sort as the API reads it: "key", or "-key" for descending. */
export function sortParam(sort) {
    if (!sort)
        return undefined;
    return sort.desc ? `-${sort.key}` : sort.key;
}
/** Query params with the empty ones left out (false booleans included: "not asked"); a sort as "key" / "-key". */
export function params(q) {
    const out = {};
    for (const [k, v] of Object.entries(q)) {
        if (v === undefined || v === null || v === '' || v === false)
            continue;
        if (k === 'sort' && typeof v === 'object') {
            out.sort = sortParam(v);
            continue;
        }
        out[k] = typeof v === 'string' ? v.trim() || undefined : v;
        if (out[k] === undefined)
            delete out[k];
    }
    return Object.keys(out).length ? out : undefined;
}
/** A page, whatever the server sent. */
export function asPage(r) {
    return {
        data: Array.isArray(r?.data) ? r.data : [],
        total: typeof r?.total === 'number' ? r.total : (r?.data?.length ?? 0),
    };
}
export function listCustomers(client, locationId, query = {}) {
    return unwrapCustomers(client.get(base(locationId), params({ ...query })), 'Failed to load customers').then((r) => ({
        data: Array.isArray(r?.data) ? r.data : [],
        total: typeof r?.total === 'number' ? r.total : (r?.data?.length ?? 0),
    }));
}
export function getCustomer(client, locationId, customerId) {
    return unwrapCustomers(client.get(customerPath(locationId, customerId)), 'Failed to load the customer');
}
export function createCustomer(client, locationId, input) {
    return unwrapCustomers(client.post(base(locationId), input), 'Failed to add the customer');
}
export function updateCustomer(client, locationId, customerId, input) {
    return unwrapCustomers(client.patch(customerPath(locationId, customerId), input), 'Failed to save the customer');
}
export function archiveCustomer(client, locationId, customerId) {
    return unwrapCustomers(client.post(`${customerPath(locationId, customerId)}/archive`), 'Failed to archive the customer');
}
export function restoreCustomer(client, locationId, customerId) {
    return unwrapCustomers(client.post(`${customerPath(locationId, customerId)}/restore`), 'Failed to restore the customer');
}
export function listCustomersByVin(client, locationId, vin) {
    return unwrapCustomers(client.get(`${base(locationId)}/by-vin/${encodeURIComponent(vin)}`), 'Failed to look up the vehicle’s customers').then((r) => (Array.isArray(r?.data) ? r.data : []));
}
export function listCustomerJobs(client, locationId, customerId, query = {}) {
    return unwrapCustomers(client.get(`${customerPath(locationId, customerId)}/work-orders`, params({ ...query })), 'Failed to load the customer’s jobs').then(asPage);
}
/** A customer's vehicles: every one, or a page when a limit is given. */
export function listCustomerVehicles(client, locationId, customerId, query = {}) {
    return unwrapCustomers(client.get(`${customerPath(locationId, customerId)}/vehicles`, params({ ...query })), 'Failed to load the customer’s vehicles').then(asPage);
}
/** The customers of a page of work orders (at most 200), by work order id. */
export function lookupWorkOrderCustomers(client, workOrderIds) {
    if (workOrderIds.length === 0)
        return Promise.resolve({});
    return unwrapCustomers(client.post('/work-orders/customer-lookup', { work_order_ids: workOrderIds.slice(0, 200) }), 'Failed to load the work orders’ customers').then((r) => Object.fromEntries((Array.isArray(r?.data) ? r.data : []).map((c) => [c.work_order_id, c])));
}
/** The work order's customer, or null. */
export function getWorkOrderCustomer(client, workOrderId) {
    return unwrapCustomers(client.get(`/work-orders/${encodeURIComponent(workOrderId)}/customer`), 'Failed to load the work order’s customer').then((r) => r?.customer ?? null);
}
/** Sets (or, with null, clears) the work order's customer. */
export function setWorkOrderCustomer(client, workOrderId, customerId) {
    return unwrapCustomers(client.put(`/work-orders/${encodeURIComponent(workOrderId)}/customer`, { customer_id: customerId }), 'Failed to change the customer').then((r) => r?.customer ?? null);
}
